import {AlertTriangle, Pencil, Plus, X} from 'lucide-react';
import {Checkbox} from '@common-web/components/ui/checkbox';
import type {CoverageIssue} from '@client/pages/public/enroll/checkout/coverageChecks';
import type {EnrollStep} from '@client/pages/public/enroll/enrollModel';

interface CoverageWarningsProps {
  issues: CoverageIssue[];
  acknowledged: Set<string>;
  onToggleAck: (id: string) => void;
  onEditCoverage: (step: EnrollStep) => void;
  onAddDependent: () => void;
  onRemoveDependent: (index: number) => void;
  onRemoveAllDependents: () => void;
}

const actionClass =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100';

/**
 * Warning section rendered above the checkout submit once checks have run. Each mismatch is a row
 * with fix actions (edit coverage / add or remove dependents) and — for acknowledgeable issues — a
 * checkbox that lets the user proceed anyway. Must-resolve issues (orphaned dependents, no coverage)
 * have no checkbox, so the submit gate only clears once they're fixed.
 */
export function CoverageWarnings({
  issues,
  acknowledged,
  onToggleAck,
  onEditCoverage,
  onAddDependent,
  onRemoveDependent,
  onRemoveAllDependents,
}: CoverageWarningsProps) {
  if (issues.length === 0) return null;

  return (
    <section className='rounded-2xl border border-amber-300 bg-amber-50 p-6'>
      <div className='flex items-center gap-2 text-amber-900'>
        <AlertTriangle className='h-5 w-5' />
        <h2 className='text-lg font-semibold'>Please review before submitting</h2>
      </div>
      <ul className='mt-4 space-y-3'>
        {issues.map((issue) => (
          <li key={issue.id} className='rounded-xl border border-amber-200 bg-white p-4'>
            <p className='text-sm text-neutral-800'>{issue.message}</p>

            <div className='mt-3 flex flex-wrap items-center gap-2'>
              {issue.editStep && (
                <button
                  type='button'
                  onClick={() => onEditCoverage(issue.editStep!)}
                  className={actionClass}
                >
                  <Pencil className='h-3.5 w-3.5' />
                  Edit coverage
                </button>
              )}
              {issue.addDependent && (
                <button type='button' onClick={onAddDependent} className={actionClass}>
                  <Plus className='h-4 w-4' />
                  Add dependent
                </button>
              )}
              {issue.removeDependentIndex !== undefined && (
                <button
                  type='button'
                  onClick={() => onRemoveDependent(issue.removeDependentIndex!)}
                  className={actionClass}
                >
                  <X className='h-4 w-4' />
                  Remove {issue.removeDependentName}
                </button>
              )}
              {issue.removeAllDependents && (
                <button type='button' onClick={onRemoveAllDependents} className={actionClass}>
                  <X className='h-4 w-4' />
                  Remove dependents
                </button>
              )}
            </div>

            {issue.acknowledgeable && issue.acknowledgeLabel && (
              <label className='mt-3 flex cursor-pointer items-start gap-2 text-sm text-neutral-700'>
                <Checkbox
                  checked={acknowledged.has(issue.id)}
                  onCheckedChange={() => onToggleAck(issue.id)}
                  className='mt-0.5'
                />
                <span>{issue.acknowledgeLabel}</span>
              </label>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
